import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import {
    PRESENCE_DURATION_OPTIONS,
    PRESENCE_STATUS_OPTIONS,
    buildPresenceUpsert,
    getPresenceStatusOption,
    resolvePresenceRow,
} from '../../lib/accountPresence';
import { CompanySwitchMfa, useCompanySwitch } from './CompanySwitcher';
import { SIDEBAR_MODES, SIDEBAR_MODE_LABELS } from './sidebarMode';

// UX pass 2026-10-25 (Val: the "Org / account" dropdown "is where the company switcher should be located").
// The "Company" section is now the company switcher: the same list and server switch as the top-bar switcher.
// One company: its name and "This is your only company". Two or more: tap one to switch (clean reload).
// The colour picker it used to hold is kept, renamed "Brand colours", under Appearance with the sidebar choice.
function CompanySection({ client, userId, landingPath, onSignOut, reload, tenantName, theme, mutedCls, itemHoverCls, dark, onClose }) {
    const { companies, busyId, error, mfaTarget, setMfaTarget, switchTo } = useCompanySwitch({
        client, userId, landingPath, onSignOut, reload, setOpen: (value) => { if (!value) onClose?.(); },
    });
    const several = companies.length >= 2;
    return (
        <div className={`mt-3 border-t pt-3 ${dark ? 'border-gray-700' : 'border-gray-100'}`} data-testid="account-company">
            <div id="account-company-title" className={`text-xs font-semibold uppercase tracking-wide ${mutedCls}`}>
                {several ? 'Switch company' : 'Company'}
            </div>
            {companies.length === 0 ? (
                <div className="mt-1 text-sm">{tenantName || 'Current organization'}</div>
            ) : (
                <ul className="mt-1 space-y-1" aria-labelledby="account-company-title">
                    {companies.map((company) => (
                        <li key={company.orgId}>
                            <button type="button"
                                className={`flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg px-2 py-2 text-left text-sm ${itemHoverCls} ${company.isActive ? (dark ? 'bg-gray-700' : 'bg-gray-100') : ''}`}
                                aria-current={company.isActive ? 'true' : undefined}
                                disabled={Boolean(busyId) || company.canEnter === false}
                                onClick={() => switchTo(company)}>
                                <span className="truncate font-medium">{company.name}</span>
                                <span className={`shrink-0 text-xs ${mutedCls}`}>
                                    {company.isActive ? 'Current' : busyId === company.orgId ? 'Opening…' : company.canEnter === false ? 'Not available' : 'Switch'}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            {companies.length === 1 && <p className={`mt-1 text-xs ${mutedCls}`}>This is your only company.</p>}
            {error && <p role="alert" className="mt-1 text-xs text-red-600">{error}</p>}
            <CompanySwitchMfa client={client} theme={theme} onSignOut={onSignOut} mfaTarget={mfaTarget} setMfaTarget={setMfaTarget} switchTo={switchTo} />
        </div>
    );
}

function AccountMenu({
    user,
    orgId,
    tenantName = '',
    brands = [],
    activeBrandId = '',
    onActiveBrandChange,
    onOpenMyNotifications,
    onLogout,
    theme = 'light',
    companyClient = null,
    landingPath = '/',
    reload,
    sidebarMode = 'hover',
    onSidebarModeChange,
}) {
    const dark = theme === 'dark';
    const menuRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState('available');
    const [durationHours, setDurationHours] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const statusOption = getPresenceStatusOption(status);
    const paletteBrands = brands.filter((brand) => brand.color_palette?.primary);

    const savePresence = useCallback(async (nextStatus, nextDuration = '') => {
        if (!user?.id || !orgId) return;
        setSaving(true);
        setError('');
        const payload = buildPresenceUpsert({
            userId: user.id,
            orgId,
            status: nextStatus,
            durationHours: nextDuration,
        });
        const { error: saveError } = await supabaseClient
            .from('staff_presence')
            .upsert(payload, { onConflict: 'user_id' });
        setSaving(false);
        if (saveError) {
            setError(saveError.message || 'Could not save status.');
            return;
        }
        setStatus(payload.status);
        setDurationHours(payload.status_expires_at ? String(nextDuration || '') : '');
    }, [orgId, user?.id]);

    useEffect(() => {
        let alive = true;
        if (!user?.id || !orgId) {
            setStatus('available');
            setDurationHours('');
            return () => { alive = false; };
        }
        (async () => {
            const { data, error: loadError } = await supabaseClient
                .from('staff_presence')
                .select('status,status_expires_at')
                .eq('user_id', user.id)
                .eq('org_id', orgId)
                .maybeSingle();
            if (!alive) return;
            if (loadError) {
                setError(loadError.message || 'Could not load status.');
                return;
            }
            const resolved = resolvePresenceRow(data);
            setStatus(resolved.status);
            setDurationHours('');
            if (resolved.expired) {
                savePresence('available', '');
            }
        })();
        return () => { alive = false; };
    }, [orgId, savePresence, user?.id]);

    useEffect(() => {
        if (!open) return undefined;
        function handleDocumentMouseDown(event) {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpen(false);
            }
        }
        document.addEventListener('mousedown', handleDocumentMouseDown);
        return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
    }, [open]);

    const buttonCls = dark
        ? 'border-gray-600 bg-gray-700 text-gray-100 hover:bg-gray-600'
        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50';
    const panelCls = dark
        ? 'bg-gray-800 border-gray-700 text-gray-100'
        : 'bg-white border-gray-200 text-gray-800';
    const mutedCls = dark ? 'text-gray-400' : 'text-gray-500';
    const itemHoverCls = dark ? 'hover:bg-gray-700' : 'hover:bg-gray-50';
    const inputCls = dark
        ? 'bg-gray-900 border-gray-600 text-gray-100'
        : 'bg-white border-gray-300 text-gray-800';

    return (
        <div className="ia-account-menu relative" ref={menuRef}>
            <button
                type="button"
                onClick={() => setOpen((current) => !current)}
                aria-label={`Account: ${user?.email || 'Account'}`}
                aria-haspopup="menu"
                aria-expanded={open}
                className={`flex items-center justify-center gap-0 md:gap-2 rounded-full border w-[44px] h-[44px] md:w-auto md:h-auto md:min-h-[44px] lg:min-h-[44px] md:px-3 md:py-1.5 text-sm ${buttonCls}`}
            >
                <span aria-hidden="true" className="md:hidden font-semibold">{(user?.email || 'A').charAt(0).toUpperCase()}</span>
                <span aria-hidden="true" className={`hidden md:block h-2.5 w-2.5 rounded-full ${statusOption.colorClass}`} />
                <span className="hidden md:block max-w-[14rem] truncate">{user?.email || 'Account'}</span>
                <span aria-hidden="true" className={`hidden md:block ${mutedCls}`}>▾</span>
            </button>
            {open && (
                <div className={`ia-account-menu absolute right-0 z-30 mt-2 w-80 max-w-[calc(100vw-1.5rem)] max-h-[calc(100dvh-5rem)] overflow-y-auto rounded-xl border p-4 shadow-xl ${panelCls}`} role="menu">
                    <div className="mb-3">
                        <div className="text-sm font-semibold truncate">{user?.email}</div>
                        <div className={`text-xs truncate ${mutedCls}`}>{tenantName || 'Current organization'}</div>
                    </div>

                    <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                            setOpen(false);
                            onOpenMyNotifications?.();
                        }}
                        className={`mb-3 w-full rounded-lg px-2 py-2 text-left text-sm font-medium ${itemHoverCls}`}
                    >
                        My Notifications
                    </button>

                    <div className={`border-t pt-3 ${dark ? 'border-gray-700' : 'border-gray-100'}`}>
                        <div className="mb-2 flex items-center justify-between gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wide">Presence status</span>
                            <span className={`inline-flex items-center gap-1 text-xs ${statusOption.textClass}`}>
                                <span aria-hidden="true" className={`h-2 w-2 rounded-full ${statusOption.colorClass}`} />
                                {statusOption.label}
                            </span>
                        </div>
                        <div className="space-y-1">
                            {PRESENCE_STATUS_OPTIONS.map((option) => (
                                <button
                                    type="button"
                                    key={option.value}
                                    onClick={() => savePresence(option.value, durationHours)}
                                    className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm ${itemHoverCls} ${status === option.value ? (dark ? 'bg-gray-700' : 'bg-gray-100') : ''}`}
                                >
                                    <span aria-hidden="true" className={`h-2.5 w-2.5 rounded-full ${option.colorClass}`} />
                                    <span>{option.label}</span>
                                </button>
                            ))}
                        </div>
                        <label className={`mt-3 block text-xs ${mutedCls}`} htmlFor="presence-duration">
                            Duration
                        </label>
                        <select
                            id="presence-duration"
                            value={durationHours}
                            onChange={(event) => {
                                const nextDuration = event.target.value;
                                setDurationHours(nextDuration);
                                if (status !== 'available') savePresence(status, nextDuration);
                            }}
                            className={`mt-1 w-full rounded-lg border px-2 py-1.5 text-sm ${inputCls}`}
                        >
                            {PRESENCE_DURATION_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => savePresence('available', '')}
                            className="mt-2 text-sm font-medium text-[var(--accent-ink)] hover:text-[var(--accent-ink-hover)]"
                        >
                            Reset status
                        </button>
                    </div>

                    <CompanySection client={companyClient} userId={user?.id} landingPath={landingPath} onSignOut={onLogout} reload={reload}
                        tenantName={tenantName} theme={theme} mutedCls={mutedCls} itemHoverCls={itemHoverCls} dark={dark} onClose={() => setOpen(false)} />

                    <div className={`mt-3 border-t pt-3 ${dark ? 'border-gray-700' : 'border-gray-100'}`} data-testid="account-appearance">
                        <div className={`text-xs font-semibold uppercase tracking-wide ${mutedCls}`}>Appearance</div>
                        <fieldset className="mt-2">
                            <legend className="text-sm font-medium">Left menu</legend>
                            <div className="mt-1 grid grid-cols-2 gap-2">
                                {SIDEBAR_MODES.map((mode) => (
                                    <label key={mode} className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border px-2 text-sm ${sidebarMode === mode ? 'border-[var(--selected-pill-border)]' : (dark ? 'border-gray-600' : 'border-gray-300')}`}>
                                        <input type="radio" name="account-sidebar-mode" value={mode} checked={sidebarMode === mode}
                                            onChange={() => onSidebarModeChange?.(mode)} />
                                        <span>{SIDEBAR_MODE_LABELS[mode]}</span>
                                    </label>
                                ))}
                            </div>
                        </fieldset>
                        {paletteBrands.length > 0 && (
                            <>
                                <label className="mt-3 block text-sm font-medium" htmlFor="account-brand-colours">Brand colours</label>
                                <select
                                    id="account-brand-colours"
                                    value={activeBrandId}
                                    onChange={(event) => onActiveBrandChange?.(event.target.value)}
                                    className={`mt-1 min-h-[44px] w-full rounded-lg border px-2 py-1.5 text-sm ${inputCls}`}
                                >
                                    <option value="">Default (Flow teal)</option>
                                    {paletteBrands.map((brand) => (
                                        <option key={brand.id} value={brand.id}>{brand.name}</option>
                                    ))}
                                </select>
                                <p className={`mt-1 text-xs ${mutedCls}`}>Changes the app colours on this device only. It does not switch company.</p>
                            </>
                        )}
                    </div>

                    <div className={`mt-3 border-t pt-3 ${dark ? 'border-gray-700' : 'border-gray-100'}`}>
                        {error && <div className="mb-2 text-xs text-red-600">{error}</div>}
                        {saving && <div className={`mb-2 text-xs ${mutedCls}`}>Saving status…</div>}
                        <button
                            type="button"
                            onClick={onLogout}
                            className="w-full rounded-lg px-3 py-2 text-left text-sm font-medium text-orange-600 hover:bg-orange-50"
                        >
                            Sign out
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AccountMenu;
